import {
  Controller,
  Get,
  Post,
  Delete,
  Param,
  UseInterceptors,
  UploadedFile,
  ParseFilePipe,
  MaxFileSizeValidator,
  FileTypeValidator,
  HttpStatus,
  Req,
  Body,
} from '@nestjs/common';
import { FileInterceptor, MulterFile } from '@webundsoehne/nest-fastify-file-upload';
import { ApiTags, ApiOperation, ApiResponse, ApiConsumes, ApiBody } from '@nestjs/swagger';
import { FastifyRequest } from 'fastify';
import { User } from '../../user/entities/user.entity';
import { FileService } from '../services/file.service';
import { FileResponseDto } from '../dto/file-response.dto';
import { UploadFileDto } from '../dto/upload-file.dto';
import { ApiResponseDto } from '../../../common/dtos/api-response.dto';
import { ConfigService } from '@nestjs/config';
import { UtilsService } from '../../../shared/utils/utils.service';

@ApiTags('Files')
@Controller('files')
export class FileController {
  constructor(
    private readonly fileService: FileService,
    private readonly configService: ConfigService,
    private readonly utilsService: UtilsService,
  ) {}

  @Post()
  @ApiOperation({ summary: 'Upload a file' })
  @ApiConsumes('multipart/form-data')
  @ApiBody({
    schema: {
      type: 'object',
      properties: {
        module: {
          type: 'string',
          description: 'The module where the file will be used',
          example: 'newsfeed',
        },
        file: {
          type: 'string',
          format: 'binary',
          example: 'file',
        },
      },
      required: ['module'],
    },
  })
  @ApiResponse({
    status: 201,
    description: 'File uploaded successfully',
    type: FileResponseDto,
  })
  @UseInterceptors(FileInterceptor('file'))
  async uploadFile(
    @UploadedFile(
      new ParseFilePipe({
        validators: [
          new MaxFileSizeValidator({ maxSize: 10 * 1024 * 1024 }), // 10MB
        //   new FileTypeValidator({ fileType: '.(png|jpeg|jpg|gif|mp4|mp3|pdf|doc|docx)' }),
        ],
      }),
    )
    file: MulterFile,
    @Body() body: UploadFileDto,
    @Req() request: FastifyRequest & { user: User },
  ): Promise<ApiResponseDto<FileResponseDto>> {
    console.log('File object:', file);
    console.log('File buffer:', file?.buffer);
    console.log('File properties:', Object.keys(file || {}));
    
    const filePath = await this.utilsService.uploadFile(file);
    const baseUrl = this.configService.get<string>('config.baseUrl');
    const fileUrl = `${baseUrl}/${filePath}`;
    
    const fileEntity = await this.fileService.create({
      url: fileUrl,
      user: request.user,
      module: body.module,
      mime_type: file.mimetype,
      module_id: null,
    });
    
    return {
      success: true,
      message: 'File uploaded successfully',
      data: fileEntity,
      statusCode: HttpStatus.CREATED,
    };
  }

  @Get()
  @ApiOperation({ summary: 'Get all files' })
  @ApiResponse({
    status: 200,
    description: 'List of all files',
    type: [FileResponseDto],
  })
  async findAll(): Promise<ApiResponseDto<FileResponseDto[]>> {
    const files = await this.fileService.findAll();
    return {
      success: true,
      message: 'Files retrieved successfully',
      data: files,
      statusCode: HttpStatus.OK,
    };
  }

  @Get(':id')
  @ApiOperation({ summary: 'Get a specific file' })
  @ApiResponse({
    status: 200,
    description: 'The file record',
    type: FileResponseDto,
  })
  @ApiResponse({ status: 404, description: 'File not found' })
  async findOne(@Param('id') id: number): Promise<ApiResponseDto<FileResponseDto>> {
    const file = await this.fileService.findOne(id);
    return {
      success: true,
      message: 'File retrieved successfully',
      data: file,
      statusCode: HttpStatus.OK,
    };
  }

  @Delete(':id')
  @ApiOperation({ summary: 'Delete a file' })
  @ApiResponse({
    status: 200,
    description: 'File deleted successfully',
  })
  @ApiResponse({ status: 404, description: 'File not found' })
  async remove(@Param('id') id: number): Promise<ApiResponseDto<void>> {
    await this.fileService.remove(id);
    return {
      success: true,
      message: 'File deleted successfully',
      statusCode: HttpStatus.OK,
      data: undefined,
    };
  }
}
